import React from "react";
import { Phone, Mail, MessageCircle, MapPin, Building2, ArrowRight } from "lucide-react";
import { CONTACT } from "@/lib/contact";

export default function ContactSection() {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#2B2420] text-white py-20 md:py-28 relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(201,162,76,0.14),transparent_55%)]" aria-hidden />
      <div className="relative px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#C9A24C] mb-5">
            <span className="w-8 h-px bg-[#C9A24C]" /> Contact
          </div>
          <h2
            className="font-black tracking-tighter leading-[0.95] text-4xl md:text-5xl lg:text-6xl text-white"
            style={{ fontFamily: "Cabinet Grotesk" }}
          >
            Get in touch.
          </h2>
          <p className="mt-5 text-white/80 max-w-md leading-relaxed">
            Call, WhatsApp, email or send photos through the form. Real people —
            no call centres, no chasing. We respond quickly and straight to the point.
          </p>
          <a
            href="#quote"
            data-testid="contact-cta"
            className="mt-8 inline-flex items-center gap-2 bg-[#C9A24C] hover:bg-[#B08C3F] text-[#2B2420] px-6 py-4 text-sm font-bold uppercase tracking-[0.15em] transition-colors"
          >
            Request a Free Quote <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Phone primary */}
          <a
            href={`tel:${CONTACT.primary.tel}`}
            data-testid="contact-phone-primary"
            className="group border border-white/15 p-6 md:p-7 hover:border-[#C9A24C] transition-colors bg-white/[0.03]"
          >
            <div className="flex items-center justify-between">
              <Phone className="w-5 h-5 text-[#C9A24C]" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#C9A24C]">Primary</span>
            </div>
            <div className="mt-6 text-[11px] uppercase tracking-[0.25em] text-white/60">
              {CONTACT.primary.name}
            </div>
            <div
              className="mt-1 font-black text-2xl md:text-3xl tracking-tight"
              style={{ fontFamily: "Cabinet Grotesk" }}
            >
              {CONTACT.primary.display}
            </div>
          </a>

          {/* Phone secondary */}
          <a
            href={`tel:${CONTACT.secondary.tel}`}
            data-testid="contact-phone-secondary"
            className="group border border-white/15 p-6 md:p-7 hover:border-[#C9A24C] transition-colors bg-white/[0.03]"
          >
            <div className="flex items-center justify-between">
              <Phone className="w-5 h-5 text-[#C9A24C]" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#C9A24C]">Secondary</span>
            </div>
            <div className="mt-6 text-[11px] uppercase tracking-[0.25em] text-white/60">
              {CONTACT.secondary.name}
            </div>
            <div
              className="mt-1 font-black text-2xl md:text-3xl tracking-tight"
              style={{ fontFamily: "Cabinet Grotesk" }}
            >
              {CONTACT.secondary.display}
            </div>
          </a>

          {/* Email */}
          <a
            href={`mailto:${CONTACT.email}`}
            data-testid="contact-email"
            className="group border border-white/15 p-6 md:p-7 hover:border-[#C9A24C] transition-colors bg-white/[0.03]"
          >
            <div className="flex items-center justify-between">
              <Mail className="w-5 h-5 text-[#C9A24C]" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#C9A24C]">Email</span>
            </div>
            <div
              className="mt-6 font-black text-lg md:text-xl tracking-tight break-all"
              style={{ fontFamily: "Cabinet Grotesk" }}
            >
              {CONTACT.email}
            </div>
          </a>

          {/* WhatsApp */}
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp"
            className="group border border-white/15 p-6 md:p-7 hover:border-[#C9A24C] transition-colors bg-white/[0.03]"
          >
            <div className="flex items-center justify-between">
              <MessageCircle className="w-5 h-5 text-[#C9A24C]" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#C9A24C]">WhatsApp</span>
            </div>
            <div
              className="mt-6 font-black text-lg md:text-xl tracking-tight"
              style={{ fontFamily: "Cabinet Grotesk" }}
            >
              Chat now · {CONTACT.primary.name}
            </div>
          </a>

          {/* Address */}
          <div className="md:col-span-2 border border-white/15 p-6 md:p-7 bg-white/[0.03]" data-testid="contact-address">
            <div className="flex items-center justify-between">
              <MapPin className="w-5 h-5 text-[#C9A24C]" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#C9A24C]">Registered address</span>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <address className="not-italic font-black text-xl md:text-2xl leading-[1.2]" style={{ fontFamily: "Cabinet Grotesk" }}>
                {CONTACT.company}<br />
                {CONTACT.address.line1}<br />
                {CONTACT.address.line2}<br />
                {CONTACT.address.city}<br />
                <span className="text-[#C9A24C]">{CONTACT.address.postcode}</span>
              </address>
              <div className="space-y-2 text-sm text-white/75">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-[#C9A24C]" />
                  <span className="uppercase tracking-[0.2em] text-[10px] text-white/50">Company No.</span>
                  <span className="font-bold">{CONTACT.legal.companyNumber}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-[#C9A24C]" />
                  <span className="uppercase tracking-[0.2em] text-[10px] text-white/50">VAT</span>
                  <span className="font-bold">{CONTACT.legal.vatNumber}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-[#C9A24C]" />
                  <span className="uppercase tracking-[0.2em] text-[10px] text-white/50">UTR</span>
                  <span className="font-bold">{CONTACT.legal.utrNumber}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
